import { FC } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import strings from '../../../common/Translation/Translate';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const PAYMENT_DATE_INPUTS = ['DD-MM-YYYY HH:mm', 'D MMM YYYY, h:mm A', 'D MMM YYYY, hh:mm A', 'YYYY-MM-DDTHH:mm:ss'];

const formatBeoPaymentDate = (value?: string) => {
  if (!value) return '—';
  const strict = dayjs(value, PAYMENT_DATE_INPUTS, true);
  if (strict.isValid()) return strict.format('D MMM YYYY, h:mm A');
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('D MMM YYYY, h:mm A') : value;
};

interface Payment {
  id?: number;
  txDate: string;
  txAmount: string;
  txMode: string;
  txReference: string;
}

interface PaymentsAccordionProps {
  payments: Payment[];
  paymentReceived: string;
  paymentDue: string;
  totalCost: string;
}

const KpiCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value?: string;
  color: string;
}) => (
  <Box
    sx={{
      flex: '1 1 120px',
      minWidth: 0,
      px: 1.25,
      py: 0.75,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.paper',
    }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', fontWeight: 400, fontSize: '0.6875rem', lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.25,
        fontWeight: 700,
        fontSize: '0.9375rem',
        lineHeight: 1.25,
        color,
        fontVariantNumeric: 'tabular-nums',
        wordBreak: 'break-word',
      }}>
      {value || '—'}
    </Typography>
  </Box>
);

const PaymentsAccordion: FC<PaymentsAccordionProps> = ({ payments, paymentReceived, paymentDue, totalCost }) => {
  const PaymentKpis = (
    <Box display="flex" flexWrap="wrap" gap={1} mt={0.75}>
      <KpiCard label={strings.beo_payment_history_amount_received} value={paymentReceived} color="#188038" />
      <KpiCard label={strings.beo_payment_history_amount_due} value={paymentDue} color="#D93025" />
      <KpiCard label={strings.beo_payment_history_total_cost} value={totalCost} color="text.primary" />
    </Box>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography fontWeight={600} variant="body1">
        {strings.beo_payment_history_header_text}
      </Typography>
      {PaymentKpis}
      {payments?.length > 0 && (
        <Box sx={{ overflowX: 'auto', mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{strings.beo_payment_history_date}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{strings.beo_payment_history_amount}</strong>
                </TableCell>
                <TableCell>
                  <strong>{strings.beo_payment_history_payment_mode}</strong>
                </TableCell>
                <TableCell>
                  <strong>{strings.beo_payment_history_payment_reference}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((item: any, index: number) => (
                <TableRow key={item.id ?? index}>
                  <TableCell>{formatBeoPaymentDate(item.txDate)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {item.txAmount}
                  </TableCell>
                  <TableCell>{item.txMode}</TableCell>
                  <TableCell>{item.txReferenceNo}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default PaymentsAccordion;
